import React from 'react';
import { Overflow, OverflowItem, Tooltip, Button } from '@fluentui/react-components';
import classNames from 'owa-classnames';
import { HEADER_BUTTONS_REGION_ID } from 'owa-header-constants';
import { observer } from 'owa-mobx-react';
import { useComputedValue } from 'owa-react-hooks/lib/useComputed';
import { BUTTON_WIDTH, CONTAINER_PADDING, MAX_VISIBLE_BUTTONS } from '../../constants/constants';
import toggleHeaderPane from '../../orchestration/toggleHeaderPane';
import type { OwaHeaderButtonControl } from '../../types/OwaHeaderButtonControl';
import isFullBleedEnabled from '../../utils/isFullBleedEnabled';
import { headerButtons } from './headerButtons';
import OwaHeaderButtonBadge from './OwaHeaderButtonBadge';
import OwaHeaderOverflowMenu from './OwaHeaderOverflowMenu';
import {
    headerButton,
    headerButtonGroup,
    overflowContainer,
    buttonBadge,
    buttonFullBleed,
    buttonIcon,
} from './OwaHeaderButtonGroup.scss';

export default observer(function OwaHeaderButtonGroup() {
    const visibleButtons = React.useMemo<OwaHeaderButtonControl[]>(
        () => headerButtons.filter(button => button.isVisible?.() ?? true),
        []
    );
    const buttonCount = Math.min(visibleButtons.length, MAX_VISIBLE_BUTTONS);

    // Calculate width once based on visible buttons
    const containerStyle = React.useMemo(() => {
        // Base width is the number of buttons times button width plus padding
        const width = buttonCount * BUTTON_WIDTH + CONTAINER_PADDING;

        return {
            width: `${width}px`,
            flex: `0 2 ${width}px`, // No grow, can shrink, base width
            minWidth: `${BUTTON_WIDTH}px`, // Minimum width for at least one button
            maxWidth: `${width}px`, // Maximum based on button count
        };
    }, [buttonCount]);

    return (
        <div style={containerStyle} className={headerButtonGroup}>
            <Overflow overflowDirection="start">
                <div id={HEADER_BUTTONS_REGION_ID} className={overflowContainer}>
                    {visibleButtons.map(button => (
                        <OwaHeaderButton key={button.id} {...button} />
                    ))}
                    <OwaHeaderOverflowMenu buttons={visibleButtons} />
                </div>
            </Overflow>
        </div>
    );
}, 'OwaHeaderButtonGroup');

const OwaHeaderButton: React.FC<OwaHeaderButtonControl> = observer(
    ({ id, renderLabel, renderIcon, onClick, pane, badge }) => {
        const handleClick = React.useCallback(() => {
            onClick?.();
            if (pane) {
                toggleHeaderPane(pane.id);
            }
        }, [pane, onClick]);

        const icon = useComputedValue(() => {
            return (
                <div className={buttonIcon}>
                    {renderIcon()}
                    {badge?.getCount && (
                        <OwaHeaderButtonBadge
                            id={id}
                            getCount={badge.getCount}
                            className={buttonBadge}
                        />
                    )}
                </div>
            );
        }, [renderIcon, id, badge?.getCount]);

        return (
            <>
                <OverflowItem key={id} id={id}>
                    <Tooltip content={renderLabel()} relationship="description">
                        <Button
                            id={id}
                            className={classNames(
                                headerButton,
                                isFullBleedEnabled() && buttonFullBleed
                            )}
                            icon={icon}
                            onClick={handleClick}
                        />
                    </Tooltip>
                </OverflowItem>
                {pane?.render && pane.render(pane.id)}
            </>
        );
    },
    'OwaHeaderButton'
);
